import * as React from "react"
import { router } from "@inertiajs/react"
import { ExternalLink, RefreshCw } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { SelectedReply } from "./types"

export function ResearchPanel({ reply }: { readonly reply: SelectedReply }) {
  const [expanded, setExpanded] = React.useState(false)
  const research = reply.research
  if (!research) return null

  const summary = summarizeResearch(research)

  return (
    <section className="rounded-lg border border-hairline bg-page p-4">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 text-left"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
      >
        <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-ink-muted">
          <span aria-hidden="true">{expanded ? "▾" : "▸"}</span>
          Research &amp; list details
        </span>
        {!expanded && summary && (
          <span className="truncate text-xs text-ink-muted">{summary}</span>
        )}
      </button>

      {expanded && (
        <div className="mt-3">
          {research.what_they_do && (
            <WhatTheyDo text={research.what_they_do} />
          )}

          {research.candidate_icps.length > 0 && (
            <CandidateIcpsTable
              candidateIcps={research.candidate_icps}
              chosenOption={research.chosen_option}
              replyId={reply.id}
            />
          )}

          {research.chosen_reason && (
            <div className="mt-4">
              <h3 className="mb-1 font-medium">Why this one</h3>
              <p className="text-sm text-ink-muted">{research.chosen_reason}</p>
            </div>
          )}

          <BuiltLists
            builtOptions={research.built_options}
            replyId={reply.id}
            chosenOption={research.chosen_option}
            draftingJob={reply.drafting_job}
            activeDraft={reply.active_draft}
          />
        </div>
      )}
    </section>
  )
}

function summarizeResearch(
  research: NonNullable<SelectedReply["research"]>,
): string | null {
  const chosen = research.chosen_option
  if (chosen) {
    const opt = research.built_options.find((o) => o.option === chosen)
    if (opt) return `Option ${opt.option} chosen · ${opt.count} leads`
    return `Option ${chosen} chosen`
  }

  if (research.built_options.length > 0) {
    const totalLeads = research.built_options.reduce((sum, o) => sum + o.count, 0)
    return `${research.built_options.length} lists built · ${totalLeads} leads`
  }

  if (research.what_they_do) {
    const firstLine = research.what_they_do.split("\n")[0]?.trim() ?? ""
    return firstLine.length > 60 ? `${firstLine.slice(0, 60)}…` : firstLine
  }

  return null
}

function WhatTheyDo({ text }: { readonly text: string }) {
  const [expanded, setExpanded] = React.useState(false)
  const long = text.length > 400
  const display = long && !expanded ? text.slice(0, 400) + "\u2026" : text

  return (
    <div className="mb-4">
      <h3 className="mb-1 font-medium">What they do</h3>
      <p className="text-sm text-ink-body">{display}</p>
      {long && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="mt-1 text-xs text-accent-display hover:underline"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  )
}

function CandidateIcpsTable({
  candidateIcps,
  chosenOption,
  replyId,
}: {
  readonly candidateIcps: readonly {
    readonly option: string
    readonly segment: string
    readonly size_geo_buyer: string
    readonly why_plausible: string
    readonly risk: string
  }[]
  readonly chosenOption: string | null
  readonly replyId: number
}) {
  return (
    <div className="mb-4">
      <h3 className="mb-2 font-medium">Candidate ICPs</h3>
      <div className="overflow-x-auto rounded-md border border-hairline">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-hairline bg-surface">
              <th className="px-3 py-2 text-left font-medium">Option</th>
              <th className="px-3 py-2 text-left font-medium">Segment</th>
              <th className="px-3 py-2 text-left font-medium">Size / Geo / Buyer</th>
              <th className="px-3 py-2 text-left font-medium">Why plausible</th>
              <th className="px-3 py-2 text-left font-medium">Risk</th>
              <th className="px-3 py-2 text-left font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {candidateIcps.map((icp) => {
              const chosen = icp.option === chosenOption
              return (
                <tr
                  key={icp.option}
                  className={`border-b border-hairline ${
                    chosen ? "bg-accent-faded ring-1 ring-accent" : ""
                  }`}
                >
                  <td className="px-3 py-2">
                    {icp.option}
                    {chosen && <Badge tone="accent" className="ml-1">chosen</Badge>}
                  </td>
                  <td className="px-3 py-2">{icp.segment}</td>
                  <td className="px-3 py-2 text-ink-muted">{icp.size_geo_buyer}</td>
                  <td className="px-3 py-2">{icp.why_plausible}</td>
                  <td className="px-3 py-2 text-ink-muted">{icp.risk}</td>
                  <td className="px-3 py-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        router.post(
                          `/replies/${replyId}/enqueue_draft`,
                          { task_type: "build_list", option: icp.option },
                          { preserveScroll: true },
                        )
                      }
                    >
                      <RefreshCw className="h-3 w-3" />
                      Rebuild
                    </Button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function BuiltLists({
  builtOptions,
  replyId,
  chosenOption,
  draftingJob,
  activeDraft,
}: {
  readonly builtOptions: readonly {
    readonly option: string
    readonly icp: string
    readonly link: string | null
    readonly count: number
  }[]
  readonly replyId: number
  readonly chosenOption: string | null
  readonly draftingJob: SelectedReply["drafting_job"]
  readonly activeDraft: SelectedReply["active_draft"]
}) {
  const [inFlightOption, setInFlightOption] = React.useState<
    string | null | undefined
  >(undefined)
  const usableOptions = builtOptions.filter((opt) => (opt.link ?? "").trim() !== "")
  const jobInFlight = ["queued", "claimed", "drafting"].includes(
    draftingJob?.status ?? "",
  )
  const isInFlight = inFlightOption !== undefined || jobInFlight
  const displayedInFlightOption =
    inFlightOption !== undefined ? inFlightOption : chosenOption

  function regenerate(chosen_option: string | null) {
    if (
      activeDraft?.generated_by === "human" &&
      !window.confirm(
        "Re-draft to feature this option? This replaces the current draft.",
      )
    ) {
      return
    }

    setInFlightOption(chosen_option)
    router.post(
      `/replies/${replyId}/regenerate`,
      { chosen_option, deliver_list: true },
      {
        preserveScroll: true,
        onFinish: () => setInFlightOption(undefined),
      },
    )
  }

  if (builtOptions.length === 0) {
    return (
      <div className="mt-4">
        <h3 className="mb-1 font-medium">Built lists</h3>
        <p className="text-sm text-ink-muted">No lists built yet</p>
      </div>
    )
  }

  return (
    <div className="mt-4">
      <h3 className="mb-2 font-medium">Built lists</h3>
      <div className="space-y-2">
        {builtOptions.map((opt) => (
          <div
            key={opt.option}
            className="flex items-center justify-between rounded-md border border-hairline bg-surface px-3 py-2 text-sm"
          >
            <span>
              <strong>{opt.option}</strong> — {opt.icp} — {opt.count} leads
            </span>
            {opt.link ? (
              <a
                href={opt.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-accent-display no-underline hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                View list
              </a>
            ) : (
              <span className="text-ink-muted">Link unavailable</span>
            )}
          </div>
        ))}
      </div>
      {usableOptions.length > 1 && (
        <div className="mt-3">
          <div className="flex flex-wrap items-center gap-2">
            {usableOptions.map((opt) => {
              const shortIcp =
                opt.icp.length > 30 ? `${opt.icp.slice(0, 30)}…` : opt.icp
              const active = opt.option === chosenOption

              return (
                <Button
                  key={opt.option}
                  type="button"
                  variant="ghost"
                  size="sm"
                  title={`${opt.option} · ${opt.icp}`}
                  className={active ? "bg-accent-faded ring-1 ring-accent" : ""}
                  disabled={isInFlight}
                  onClick={() => regenerate(opt.option)}
                >
                  {opt.option} · {shortIcp}
                </Button>
              )
            })}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={
                chosenOption === null
                  ? "bg-accent-faded ring-1 ring-accent"
                  : ""
              }
              disabled={isInFlight}
              onClick={() => regenerate(null)}
            >
              Both
            </Button>
          </div>
          {isInFlight && (
            <p className="mt-2 text-xs text-ink-muted">
              {displayedInFlightOption === null
                ? "Re-drafting with both options…"
                : `Re-drafting to feature option ${displayedInFlightOption}…`}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
